import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Video, CalendarPlus } from "lucide-react";
import { recall, assetUrl } from "@/lib/forms";
import BookedBurst from "@/components/forms/BookedBurst";

const VARIANTS = {
  fade: { initial: { opacity: 0 }, animate: { opacity: 1 } },
  slide: { initial: { opacity: 0, y: 60 }, animate: { opacity: 1, y: 0 } },
  zoom: { initial: { opacity: 0, scale: 0.85 }, animate: { opacity: 1, scale: 1 } },
};

const gstamp = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

export function gcalUrl(m) {
  const s = new Date(m.start);
  const e = new Date(s.getTime() + (Number(m.duration_min) || 30) * 60000);
  const details = [m.description, m.meet_url && `Join: ${m.meet_url}`, m.manage_url && `Reschedule or cancel: ${m.manage_url}`].filter(Boolean).join("\n\n");
  const p = new URLSearchParams({ action: "TEMPLATE", text: m.title || "Call", dates: `${gstamp(s)}/${gstamp(e)}`, details });
  if (m.meet_url) p.set("location", m.meet_url);
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

export default function Ending({ ending, meeting, answers, accent, radius, preview }) {
  const v = VARIANTS[ending.transition] || VARIANTS.fade;
  useEffect(() => {
    if (preview || !ending.redirect_url) return undefined;
    const t = setTimeout(() => { window.location.href = ending.redirect_url; }, (Number(ending.redirect_delay_s) || 0) * 1000);
    return () => clearTimeout(t);
  }, [ending, preview]);
  return (
    <motion.div key={ending.id} {...v} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="max-w-xl" data-testid={`ending-${ending.id}`}>
      <div data-testid="thank-you-screen">
        {meeting?.success_text && <BookedBurst text={meeting.success_text} accent={accent} />}
        {ending.image_url && <img src={assetUrl(ending.image_url)} alt="" className="max-h-56 rounded-md mb-6 object-cover" />}
        <h1 className="text-3xl sm:text-4xl font-medium tracking-tight">{recall(ending.title, answers) || "Thanks!"}</h1>
        {ending.body && <p className="opacity-70 mt-3 text-lg whitespace-pre-line">{recall(ending.body, answers)}</p>}
        {ending.show_meet !== false && meeting && (
          <div className="mt-6 p-4 rounded-md border flex items-start gap-3" style={{ borderColor: `${accent}55` }} data-testid="ty-meeting">
            <Video size={18} style={{ color: accent }} className="mt-0.5" />
            <div>
              <div className="text-sm">{new Date(meeting.start).toLocaleString("en-IN", { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" })} IST</div>
              {meeting.meet_url ? <a href={meeting.meet_url} target="_blank" rel="noreferrer" className="text-sm underline break-all" style={{ color: accent }} data-testid="ty-meet-link">{meeting.meet_url}</a>
                : <div className="text-xs opacity-60 mt-1">We'll send the calendar invite to your email shortly.</div>}
              {meeting.confirmed && <div className="text-xs opacity-60 mt-1">A calendar invite is on its way to your inbox.</div>}
              {meeting.manage_url && <a href={meeting.manage_url} className="text-xs underline opacity-70 mt-1 inline-block" style={{ color: accent }} data-testid="ty-manage-link">Reschedule or cancel</a>}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-3 mt-6">
          {ending.add_to_calendar !== false && meeting?.start && (
            <a href={gcalUrl(meeting)} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 px-5 py-2.5 font-medium border ${radius}`} style={{ borderColor: accent, color: accent }} data-testid="ty-gcal-button">
              <CalendarPlus size={16} />{ending.calendar_label || "Add to Google Calendar"}
            </a>
          )}
          {ending.button_url && (
            <a href={ending.button_url} target={ending.button_new_tab ? "_blank" : undefined} rel="noreferrer" className={`inline-flex items-center gap-2 px-5 py-2.5 font-medium text-black ${radius}`} style={{ background: accent }} data-testid="ty-button">
              {ending.button_label || "Continue"}<ArrowRight size={16} />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}
